import type { OrderListItem } from '@agos/api-client/model'
import { T, cityFromAddress, formatDayMonth, formatTime, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import {
  Bell,
  CalendarClock,
  CheckCircle2,
  Clock,
  ConciergeBell,
  MapPin,
  Package,
  PlayCircle,
  ShoppingBag,
  Store,
  Truck,
} from 'lucide-react'

import {
  ELAPSED_BG_COLORS,
  ELAPSED_TEXT_COLORS,
  elapsedBand,
  elapsedFormatted,
  formatCountdown,
  minutesUntil,
  type ItemProgress,
} from './elapsed'
import { KitchenItemCard, type KitchenDescriptions } from './KitchenItemCard'
import { groupOrderItems, type KitchenLine } from './kitchen-items'

/**
 * One ticket on the kitchen display.
 *
 * Everything about it is sized for a tablet on a wall being read from a metre
 * away: the order number is the largest thing on the card, the channel is a
 * colour strip rather than a word, and the elapsed-time bar changes colour
 * before it changes text. §14.9 — this runs on the restaurant's tablets, and a
 * card that only works at desktop width fails where the food is made.
 */

const TYPE_STRIP_COLORS: Record<string, string> = {
  delivery: 'bg-blue-600',
  restaurant: 'bg-red-600',
  bar: 'bg-emerald-600',
}

const TYPE_SHORT: Record<string, string> = {
  delivery: 'ROZ',
  restaurant: 'RES',
  bar: 'OSB',
}

const TYPE_ICONS: Record<string, typeof Store> = {
  delivery: Truck,
  restaurant: Store,
  bar: ShoppingBag,
}

/** Inside this many minutes the scheduled countdown turns red. */
const IMMINENT_MINUTES = 15

export interface KitchenOrderCardProps {
  order: OrderListItem
  lines: KitchenLine[]
  descriptions: KitchenDescriptions
  now: number
  preorderThresholdMinutes: number
  /** Held on screen for a moment after it was handed over. */
  isCompleting: boolean
  isBusy: boolean
  canAccept: boolean
  progressOf: (key: string) => ItemProgress
  onToggleItem: (key: string) => void
  onAdvance: (order: OrderListItem) => void
}

export function KitchenOrderCard({
  order,
  lines,
  descriptions,
  now,
  preorderThresholdMinutes,
  isCompleting,
  isBusy,
  canAccept,
  progressOf,
  onToggleItem,
  onAdvance,
}: KitchenOrderCardProps) {
  const isNew = order.status === 'new' && !isCompleting
  const stripColor = TYPE_STRIP_COLORS[order.type ?? ''] ?? TYPE_STRIP_COLORS.restaurant
  const TypeIcon = TYPE_ICONS[order.type ?? ''] ?? Store
  const itemCount = lines.reduce((total, line) => total + (line.quantity ?? 1), 0)
  const band = elapsedBand(order.createdAt ?? '', now)

  const minutesToSchedule = order.scheduledAt ? minutesUntil(order.scheduledAt, now) : null
  const isDueSoon =
    order.isPreorder === true &&
    minutesToSchedule !== null &&
    minutesToSchedule <= preorderThresholdMinutes &&
    minutesToSchedule > 0

  // Doobjednávky: a table that ordered again while the first round was cooking.
  // The rounds render newest-last, and the earlier ones fade — they are already
  // on the pass and are shown only so the cook knows what the table has.
  const rounds = [...new Set(lines.map((line) => roundOf(line)))].sort(
    (first, second) => first - second,
  )
  const latestRound = rounds[rounds.length - 1] ?? 1

  return (
    <div
      className={cn(
        'w-full flex flex-col rounded-lg overflow-hidden transition-all duration-500',
        'bg-[#161622] border border-white/8',
        isCompleting && 'opacity-40 scale-[0.97]',
      )}
    >
      <div className={cn('px-3 py-1.5 flex items-center justify-between', stripColor)}>
        <div className="flex items-center gap-1.5">
          <TypeIcon className="w-3.5 h-3.5 text-white/80" />
          <span className="text-[11px] font-black text-white tracking-widest">
            {TYPE_SHORT[order.type ?? ''] ?? (order.type ?? '').toUpperCase().slice(0, 3)}
          </span>
        </div>
        <span className="text-[10px] font-bold text-white/70">
          {itemCount} {tt('pol.')}
        </span>
      </div>

      <div className="px-3 pt-2.5 pb-1 bg-[#12121e]">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1',
                isCompleting ? 'bg-emerald-400' : isNew ? 'bg-red-400 animate-pulse' : 'bg-blue-400',
              )}
            />
            <span className="font-mono text-3xl font-black text-white leading-none tracking-tight">
              #{order.orderNumber?.split('-').pop()}
            </span>
          </div>
          <p className="text-[10px] text-white/30 font-medium">
            {order.createdAt ? formatTime(order.createdAt) : ''}
          </p>
        </div>
      </div>

      <div
        className={cn(
          'mx-3 mt-1 mb-1.5 rounded flex items-center justify-center gap-1.5 py-1.5',
          ELAPSED_BG_COLORS[band],
        )}
      >
        <Clock className={cn('w-3.5 h-3.5', ELAPSED_TEXT_COLORS[band])} />
        <span className={cn('text-sm font-black tabular-nums', ELAPSED_TEXT_COLORS[band])}>
          {elapsedFormatted(order.createdAt ?? '', now)}
        </span>
        {isDueSoon && minutesToSchedule !== null && (
          <>
            <span className="text-white/15 mx-1">|</span>
            <Bell className="w-3 h-3 text-amber-400" />
            <span className="text-[11px] font-black text-amber-400">
              {formatCountdown(minutesToSchedule)}
            </span>
          </>
        )}
      </div>

      <div className="flex flex-col gap-1.5 px-0">
        {order.isPreorder === true && order.scheduledAt && minutesToSchedule !== null && (
          <div className="mx-3 flex items-center gap-1.5 bg-amber-500/12 border border-amber-500/20 rounded px-2 py-1.5 text-[11px] text-amber-400">
            <CalendarClock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              <strong>
                {formatDayMonth(order.scheduledAt)} {formatTime(order.scheduledAt)}
              </strong>
              {' · '}
              <span
                className={cn('font-bold', minutesToSchedule <= IMMINENT_MINUTES && 'text-red-400')}
              >
                {formatCountdown(minutesToSchedule)}
              </span>
            </span>
          </div>
        )}

        {order.type === 'delivery' && (order.customerName || order.customerAddress) && (
          <div className="mx-3 space-y-0.5 bg-white/3 rounded px-2 py-1.5">
            {order.customerName && (
              <p className="text-[11px] text-white/40 leading-tight">{order.customerName}</p>
            )}
            {order.customerAddress && (
              <div className="flex items-start gap-1">
                <MapPin className="w-3 h-3 text-white/40 shrink-0 mt-0.5" />
                {/* The town, not the street: the cook wants to know which
                    village the driver is going to, not the house number. */}
                <p className="text-[11px] font-black text-white/70 leading-tight">
                  {cityFromAddress(order.customerAddress)}
                </p>
              </div>
            )}
          </div>
        )}

        {(order.employeeName ?? order.driverName) && (
          <div className="mx-3 flex items-center gap-1 text-[10px] text-violet-300 bg-violet-500/12 rounded px-2 py-1 w-fit">
            <ConciergeBell className="w-3 h-3" />
            {order.employeeName ?? order.driverName}
          </div>
        )}

        {order.notes && (
          <div className="mx-3 mb-1 bg-amber-500/12 border border-amber-500/20 rounded px-2 py-1.5 text-[11px] font-semibold text-amber-400">
            📝 {order.notes}
          </div>
        )}

        <div className="border-t border-white/5">
          {rounds.map((round) => {
            const roundLines = lines.filter((line) => roundOf(line) === round)
            const isEarlierRound = round < latestRound
            return (
              <div key={round} className={cn(isEarlierRound && 'opacity-35')}>
                {latestRound > 1 && (
                  <div
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-0.5 border-b',
                      isEarlierRound
                        ? 'bg-white/3 border-white/5'
                        : 'bg-orange-500/12 border-orange-500/15',
                    )}
                  >
                    <span
                      className={cn(
                        'text-[9px] font-black uppercase tracking-widest',
                        isEarlierRound ? 'text-white/20' : 'text-orange-400',
                      )}
                    >
                      {isEarlierRound
                        ? `${tt('Kolo')} ${round}`
                        : `${tt('Doobjednávka (kolo')} ${round})`}
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-1.5 px-2 py-1.5">
                  {groupOrderItems(roundLines).map((entry, entryIndex) =>
                    entry.type === 'box' ? (
                      <div
                        key={entry.groupId}
                        data-kitchen-box={entry.groupId}
                        className="flex flex-col rounded-lg border border-white/8 overflow-hidden bg-white/3"
                      >
                        <div className="flex items-center gap-1 px-2.5 py-1 bg-white/4">
                          <Package className="w-3 h-3 text-white/35 flex-shrink-0" />
                          <span className="text-[10px] font-black text-white/60 uppercase tracking-wide">
                            {entry.groupName}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1 p-1.5">
                          {entry.lines.map((line, slotIndex) => {
                            const key = itemKey(order.id ?? 0, entryIndex, slotIndex)
                            return (
                              <KitchenItemCard
                                key={key}
                                line={line}
                                descriptions={descriptions}
                                progress={progressOf(key)}
                                onToggle={() => onToggleItem(key)}
                              />
                            )
                          })}
                        </div>
                      </div>
                    ) : (
                      <KitchenItemCard
                        key={itemKey(order.id ?? 0, entryIndex)}
                        line={entry.line}
                        descriptions={descriptions}
                        progress={progressOf(itemKey(order.id ?? 0, entryIndex))}
                        onToggle={() => onToggleItem(itemKey(order.id ?? 0, entryIndex))}
                      />
                    ),
                  )}
                  {roundLines.length === 0 && (
                    <p className="text-[11px] text-white/20 px-2.5 py-1.5">
                      <T>Žiadne položky</T>
                    </p>
                  )}
                </div>
              </div>
            )
          })}
          {lines.length === 0 && (
            <p className="text-[11px] text-white/20 px-2.5 py-1.5">
              <T>Žiadne položky</T>
            </p>
          )}
        </div>
      </div>

      <div className="p-2.5">
        {isCompleting ? (
          <div className="w-full py-3 rounded-lg bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center gap-2 text-[15px] font-black text-emerald-400 uppercase tracking-wider">
            <CheckCircle2 className="w-5 h-5" /> <T>Odovzdaná</T>
          </div>
        ) : isNew && !canAccept ? (
          <div className="w-full py-3 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center gap-2 text-[13px] font-bold text-white/30 uppercase tracking-wider">
            <PlayCircle className="w-4 h-4" /> <T>Bez oprávnenia</T>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onAdvance(order)}
            disabled={isBusy}
            className={cn(
              'w-full py-3 rounded-lg font-black text-[15px] uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-50 active:scale-95',
              isNew
                ? 'bg-green-500 hover:bg-green-400 text-white shadow-lg shadow-green-500/30'
                : 'bg-rose-500 hover:bg-rose-400 text-white shadow-lg shadow-rose-500/30',
            )}
          >
            {isNew ? (
              <>
                <PlayCircle className="w-5 h-5" /> <T>Prijatá</T>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" /> <T>Hotová</T>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

function roundOf(line: KitchenLine): number {
  return line.kitchenRound ?? 1
}

/** Stable per order, per entry, per slot — the key a cook's tap is stored under. */
export function itemKey(orderId: number, entryIndex: number, slotIndex?: number): string {
  return slotIndex === undefined
    ? `${orderId}-${entryIndex}`
    : `${orderId}-${entryIndex}-${slotIndex}`
}
